import React, { useState } from 'react';
import { FiX, FiClock } from 'react-icons/fi';
import { GESTION_TIPOS, agingLabel, agingTone } from '../utils/cobranzas';
import { formatMoney } from '../utils/money';

function formatFecha(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function gestionTipoLabel(tipo) {
  return GESTION_TIPOS.find((t) => t.id === tipo)?.label || tipo;
}

export default function CobranzaGestionesModal({
  row,
  modoOscuro,
  onClose,
  onSave,
  saving = false,
  readOnly = false,
}) {
  const [gestionTipo, setGestionTipo] = useState('llamado');
  const [gestionTexto, setGestionTexto] = useState('');
  const [promesaFecha, setPromesaFecha] = useState('');

  if (!row) return null;

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-700';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const inputClass = modoOscuro
    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400';
  const cardInner = modoOscuro ? 'bg-gray-800/80' : 'bg-gray-50';

  const facturaStub = {
    numero: row.numero,
    razonSocial: row.cliente,
    total: row.total,
    moneda: row.moneda,
    vencimiento: row.vencimiento,
    fecha: row.fecha,
    pagos: row.pagado > 0 ? [{ monto: row.pagado }] : [],
  };
  const aging = agingLabel(facturaStub);
  const gestiones = Array.isArray(row.gestiones) ? row.gestiones : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave({
      tipo: gestionTipo,
      texto: gestionTexto,
      promesaFecha: gestionTipo === 'promesa' ? promesaFecha : null,
    });
    setGestionTexto('');
    setPromesaFecha('');
    setGestionTipo('llamado');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className={`rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[92dvh] overflow-hidden border flex flex-col ${box}`}>
        <div className={`px-4 py-3 border-b flex items-start justify-between gap-3 ${divide}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FiClock className={`w-4 h-4 shrink-0 ${textMuted}`} />
              <h2 className={`text-sm font-semibold ${textPri}`}>Historial de gestiones</h2>
            </div>
            <p className={`text-xs mt-1 ${textMuted}`}>
              N°{row.numero} · {row.cliente}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className={`text-sm font-bold tabular-nums ${textPri}`}>
                {formatMoney(row.saldo, row.moneda || 'ARS')}
              </span>
              {aging && (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${agingTone(facturaStub, modoOscuro)}`}>
                  {aging}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={modoOscuro ? 'p-2 rounded-full hover:bg-gray-800 text-gray-300' : 'p-2 rounded-full hover:bg-gray-100 text-gray-600'}
            aria-label="Cerrar"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div>
            <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${textMuted}`}>
              Gestiones ({gestiones.length})
            </h3>
            {gestiones.length === 0 ? (
              <p className={`text-sm ${textMuted}`}>Todavía no hay gestiones registradas.</p>
            ) : (
              <ul className="space-y-2">
                {gestiones.map((g) => (
                  <li key={g.id || `${g.tipo}-${g.fecha}-${g.createdAt}`} className={`px-3 py-2.5 rounded-xl text-sm ${cardInner}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className={`font-semibold ${textPri}`}>{gestionTipoLabel(g.tipo)}</span>
                      <span className={`text-[11px] shrink-0 ${textMuted}`}>{formatFecha(g.fecha || g.createdAt)}</span>
                    </div>
                    {g.texto && <p className={`mt-1 text-xs ${textSec}`}>{g.texto}</p>}
                    {g.promesaFecha && (
                      <p className={`mt-1 text-xs ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
                        Prometió: {formatFecha(g.promesaFecha)}
                      </p>
                    )}
                    {g.automatico && (
                      <p className={`mt-1 text-[10px] uppercase tracking-wide ${textMuted}`}>Automática</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!readOnly && (
          <form onSubmit={handleSubmit} className={`rounded-xl border p-3 space-y-3 ${divide}`}>
            <h3 className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>Registrar gestión</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select
                value={gestionTipo}
                onChange={(e) => setGestionTipo(e.target.value)}
                className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
              >
                {GESTION_TIPOS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              {gestionTipo === 'promesa' && (
                <input
                  type="date"
                  value={promesaFecha}
                  onChange={(e) => setPromesaFecha(e.target.value)}
                  className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                  required
                />
              )}
            </div>
            <textarea
              value={gestionTexto}
              onChange={(e) => setGestionTexto(e.target.value)}
              rows={2}
              placeholder="Qué pasó: lo llamé, prometió el viernes…"
              className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
            />
            <button
              type="submit"
              disabled={saving || (gestionTipo === 'promesa' && !promesaFecha)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar gestión'}
            </button>
          </form>
          )}
          {readOnly && (
            <p className={`text-xs ${textMuted}`}>
              Modo solo lectura por mora: podés consultar el historial, pero no registrar gestiones hasta regularizar el pago.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
